import { axiosInterceptor } from "app/services/http-services/request-interceptor";
import {
    FlussDefinition,
    FlussIncident,
    FlussInstanceDetail,
    FlussInstanceSummary,
    FlussTask,
    FlussValidationError,
    FlussValidationResult,
} from "modules/fluss/types/fluss";
import type { FlussDataCatalog } from "modules/fluss/types/data-catalog";

interface ApiEnvelope<T> {
    success: boolean;
    data: T;
    meta: Record<string, unknown>;
    error?: {
        code: string;
        message: string;
        details?: unknown;
    } | null;
}

export interface SaveFlussDefinitionInput {
    xml_draft: string;
    lock_version: number;
    name?: string;
    description?: string | null;
    owner_user_id?: string | null;
}

export const flussApi = {
    async listDefinitions() {
        const response = await axiosInterceptor.get<ApiEnvelope<FlussDefinition[]>>("/api/fluss/definitions");
        return response.data.data;
    },

    async listStartableDefinitions() {
        const response = await axiosInterceptor.get<ApiEnvelope<Pick<FlussDefinition, "id" | "definition_key" | "name" | "startable_manually" | "start_inputs">[]>>(
            "/api/fluss/definitions", { params: { startable: true } }
        );
        return response.data.data;
    },

    async getDefinition(id: string) {
        const response = await axiosInterceptor.get<ApiEnvelope<FlussDefinition>>(`/api/fluss/definitions/${id}`);
        return response.data.data;
    },

    async createDefinition(payload: {
        definition_key: string;
        name: string;
        description?: string;
        xml_draft: string;
    }) {
        const response = await axiosInterceptor.post<ApiEnvelope<FlussDefinition>>("/api/fluss/definitions", payload);
        return response.data.data;
    },

    async saveDefinition(id: string, payload: SaveFlussDefinitionInput) {
        const response = await axiosInterceptor.patch<ApiEnvelope<FlussDefinition>>(
            `/api/fluss/definitions/${id}`,
            payload
        );
        return response.data.data;
    },

    async validateDefinition(id: string, xml_draft: string) {
        const response = await axiosInterceptor.post<ApiEnvelope<FlussValidationResult>>(
            `/api/fluss/definitions/${id}/validate`,
            { xml_draft }
        );
        return response.data.data;
    },

    async discardDraft(id: string, payload: { lock_version: number }) {
        const response = await axiosInterceptor.post<ApiEnvelope<FlussDefinition>>(
            `/api/fluss/definitions/${id}/discard_draft`,
            payload
        );
        return response.data.data;
    },

    async publishDefinition(id: string) {
        const response = await axiosInterceptor.post<
            ApiEnvelope<{ definition: FlussDefinition; errors: FlussValidationError[] }>
        >(`/api/fluss/definitions/${id}/publish`);
        return response.data.data;
    },

    async createNewVersion(definitionKey: string) {
        const response = await axiosInterceptor.post<ApiEnvelope<FlussDefinition>>(
            "/api/fluss/definitions/new_version",
            { definition_key: definitionKey }
        );
        return response.data.data;
    },

    async disableDefinition(id: string) {
        const response = await axiosInterceptor.post<ApiEnvelope<FlussDefinition>>(
            `/api/fluss/definitions/${id}/disable`
        );
        return response.data.data;
    },

    async enableDefinition(id: string) {
        const response = await axiosInterceptor.post<ApiEnvelope<FlussDefinition>>(
            `/api/fluss/definitions/${id}/enable`
        );
        return response.data.data;
    },

    async deleteDefinition(id: string) {
        await axiosInterceptor.delete<ApiEnvelope<{ deleted: boolean }>>(`/api/fluss/definitions/${id}`);
    },
    async listTasks(status?: string) {
        const response = await axiosInterceptor.get<ApiEnvelope<FlussTask[]>>("/api/fluss/tasks", {
            params: status ? { status } : undefined,
        });
        return response.data.data;
    },

    async getTask(id: string) {
        const response = await axiosInterceptor.get<ApiEnvelope<FlussTask>>(`/api/fluss/tasks/${id}`);
        return response.data.data;
    },

    async completeTask(id: string, outcome: string, data: Record<string, unknown> = {}) {
        const response = await axiosInterceptor.post<ApiEnvelope<FlussTask>>(
            `/api/fluss/tasks/${id}/complete`,
            { outcome, data }
        );
        return response.data.data;
    },

    async reassignTask(id: string, assigneeUserId: string) {
        const response = await axiosInterceptor.post<ApiEnvelope<FlussTask>>(
            `/api/fluss/tasks/${id}/reassign`,
            { assignee_user_id: assigneeUserId }
        );
        return response.data.data;
    },

    async listRequests(params: { status?: string; search?: string } = {}) {
        const response = await axiosInterceptor.get<ApiEnvelope<FlussInstanceSummary[]>>(
            "/api/fluss/requests",
            { params }
        );
        return response.data.data;
    },

    async getRequest(id: string) {
        const response = await axiosInterceptor.get<ApiEnvelope<FlussInstanceDetail>>(
            `/api/fluss/requests/${id}`
        );
        return response.data.data;
    },

    async listInstances(params: { status?: string; definition_key?: string; search?: string } = {}) {
        const response = await axiosInterceptor.get<ApiEnvelope<FlussInstanceSummary[]>>(
            "/api/fluss/instances",
            { params }
        );
        return response.data.data;
    },

    async startInstance(definitionKey: string, variables?: Record<string, string | number | boolean>) {
        const response = await axiosInterceptor.post<ApiEnvelope<FlussInstanceSummary>>(
            "/api/fluss/instances", { definition_key: definitionKey, ...(variables ? { variables } : {}) }
        );
        return response.data.data;
    },

    async getInstance(id: string) {
        const response = await axiosInterceptor.get<ApiEnvelope<FlussInstanceDetail>>(
            `/api/fluss/instances/${id}`
        );
        return response.data.data;
    },

    async listIncidents(definitionKey?: string) {
        const response = await axiosInterceptor.get<ApiEnvelope<FlussIncident[]>>(
            "/api/fluss/incidents",
            { params: definitionKey ? { definition_key: definitionKey } : undefined }
        );
        return response.data.data;
    },

    async retryIncident(instanceId: string, tokenId?: string) {
        const response = await axiosInterceptor.post<ApiEnvelope<FlussInstanceSummary>>(
            `/api/fluss/instances/${instanceId}/retry_incident`,
            tokenId ? { token_id: tokenId } : {}
        );
        return response.data.data;
    },

    async cancelInstance(instanceId: string, reason?: string) {
        const response = await axiosInterceptor.post<ApiEnvelope<FlussInstanceSummary>>(
            `/api/fluss/instances/${instanceId}/cancel`,
            { reason }
        );
        return response.data.data;
    },

    async suspendInstance(instanceId: string, reason?: string) {
        const response = await axiosInterceptor.post<ApiEnvelope<FlussInstanceSummary>>(
            `/api/fluss/instances/${instanceId}/suspend`,
            { reason }
        );
        return response.data.data;
    },

    async resumeInstance(instanceId: string) {
        const response = await axiosInterceptor.post<ApiEnvelope<FlussInstanceSummary>>(
            `/api/fluss/instances/${instanceId}/resume`
        );
        return response.data.data;
    },
};

// PRIM-PILOT-066 (slice 1A): authoring-time-only discovery of "resultados de nós anteriores" for
// the condition builder. flow.nodes/flow.edges must come from the Studio's live canvas
// (bpmn-js elementRegistry) — the backend has no other way to see unsaved edits, so this is a
// POST with the graph in the body, not a GET keyed by definition id/version (Fluss::
// AvailableVariables docstring, .codex/02-contracts/fluss-engine.md).
// PRIM-PILOT-068 (slice 1B-1): optional, additive to the 1A shape -- omitting config keeps the
// exact 1A behavior (human-task outcomes only). When present, only this subset is read by the
// backend (Fluss::Api::MetadataController#available_variables's permit); it mirrors the same
// config keys FactoryActionBinding/ServiceOutputBinding already read at runtime, not a new
// contract.
// PRIM-PILOT-072 (slice 1B-4a): businessRule/business_rule/businessRuleId/business_rule_id close
// the TypeScript-side counterpart of the PRIM-PILOT-071 (1B-3b) backend permit gap -- the backend
// already whitelists and reads these 4 key variants (metadata_controller.rb's available_variables
// permit), this type just needs to stop dropping them before they ever reach the request body.
export interface FlussFlowGraphNodeConfig {
    actionId?: string;
    action_id?: string;
    businessRule?: string;
    business_rule?: string;
    businessRuleId?: string;
    business_rule_id?: string;
    params?: Record<string, unknown>;
    variableMappings?: Record<string, unknown>;
    variable_mappings?: Record<string, unknown>;
    target_table?: string;
    targetTable?: string;
    field_mappings?: string | Record<string, unknown>;
    fieldMappings?: string | Record<string, unknown>;
    formId?: string;
    formMode?: string;
    form_id?: string;
    // Read by the backend's Fluss::UserTaskWaitBinding.wait_for_completion? (via
    // Fluss::AvailableVariables#outcome_typing) to know a condition-driven user_task has no
    // outcome vocabulary at all -- it completes via CompleteTaskByCondition with outcome: nil,
    // no operator ever picks a value.
    waitForCompletion?: string | boolean;
    table?: string;
    triggerType?: string;
    initialVariables?: Record<string, unknown>;
    startInputs?: { name: string; label?: string; type?: string; required?: boolean }[];
    definitionKey?: string;
    versionPolicy?: string;
    pinnedVersion?: string;
}

export interface FlussFlowGraphNode {
    id: string;
    type: string;
    label?: string;
    config?: FlussFlowGraphNodeConfig;
}

export interface FlussFlowGraphEdge {
    source: string;
    target: string;
}

export interface FlussFlowGraph {
    nodes: FlussFlowGraphNode[];
    edges: FlussFlowGraphEdge[];
}

export type { FlussCatalogOutput, FlussCatalogNode, FlussDataCatalog } from "modules/fluss/types/data-catalog";
export { EMPTY_DATA_CATALOG } from "modules/fluss/types/data-catalog";

export const fetchDataCatalog = async (
    flow: FlussFlowGraph,
    nodeId: string,
    definitionKey?: string
): Promise<FlussDataCatalog> => {
    const response = await axiosInterceptor.post<ApiEnvelope<FlussDataCatalog>>(
        "/api/fluss/metadata/available_variables",
        { flow, node_id: nodeId, ...(definitionKey ? { definition_key: definitionKey } : {}) }
    );
    return response.data.data;
};

export const flussApiError = (error: any) => ({
    code: String(error?.response?.data?.error?.code || "UNKNOWN"),
    message: String(error?.response?.data?.error?.message || error?.message || ""),
    details: error?.response?.data?.error?.details as Record<string, string> | undefined,
});
